import type { ChannelMetricsResponse } from '@/app/api/connectors/catalog/metrics/route'
import {
  FALLBACK_OVERVIEW,
  type OverviewChannel,
  type OverviewChannelKey,
} from '@/lib/overview/fallback-data'

type CatalogSubchannel = {
  key: string
  name: string
  connection: { status: string | null } | null
}

type CatalogChannel = {
  key: string
  name: string
  subchannels: CatalogSubchannel[]
}

export type OverviewCatalogResponse = {
  channels?: CatalogChannel[]
}

const OVERVIEW_CHANNEL_META: Record<OverviewChannelKey, Pick<OverviewChannel, 'hex' | 'gradient'>> =
  FALLBACK_OVERVIEW.reduce(
    (acc, channel) => {
      acc[channel.key] = {
        hex: channel.hex,
        gradient: channel.gradient,
      }
      return acc
    },
    {} as Record<OverviewChannelKey, Pick<OverviewChannel, 'hex' | 'gradient'>>,
  )

const OVERVIEW_KEYS = new Set<OverviewChannelKey>(
  FALLBACK_OVERVIEW.map((channel) => channel.key),
)

function isOverviewKey(key: string): key is OverviewChannelKey {
  return OVERVIEW_KEYS.has(key as OverviewChannelKey)
}

function centsToDisplayAmount(cents: number): number {
  return cents / 100
}

export function adaptOverviewData(
  catalog: OverviewCatalogResponse,
  metrics: ChannelMetricsResponse,
): OverviewChannel[] {
  const channels = catalog.channels ?? []
  const channelMetrics = new Map(
    (metrics.by_channel ?? []).map((row) => [row.channel_key, row]),
  )
  const subchannelName = new Map<string, string>()

  for (const channel of channels) {
    for (const subchannel of channel.subchannels ?? []) {
      subchannelName.set(subchannel.key, subchannel.name)
    }
  }

  return channels.filter((channel): channel is CatalogChannel & { key: OverviewChannelKey } =>
    isOverviewKey(channel.key),
  ).map((channel) => {
    const metric = channelMetrics.get(channel.key)
    const spend = centsToDisplayAmount(metric?.spend_cents ?? 0)
    const revenue = metric?.purchase_revenue_cents ?? 0
    const roas = metric?.spend_cents && metric.spend_cents > 0
      ? revenue / metric.spend_cents
      : 0
    const subs = (metrics.by_subchannel ?? [])
      .filter((row) => row.channel_key === channel.key)
      .sort((a, b) => b.spend_cents - a.spend_cents)
      .slice(0, 5)
      .map((row) => ({
        name: subchannelName.get(row.subchannel_key) ?? row.subchannel_key,
        spend: centsToDisplayAmount(row.spend_cents),
        roas: row.roas ?? 0,
      }))

    return {
      key: channel.key,
      name: channel.name,
      live: metric?.active_campaign_count ?? 0,
      total: (channel.subchannels ?? []).length,
      spend,
      roas,
      subs,
      ...OVERVIEW_CHANNEL_META[channel.key],
    }
  })
}
